import { inject, Injectable } from '@angular/core';
import { Firestore, collectionData, collection, CollectionReference, addDoc, DocumentReference, doc, setDoc, onSnapshot, Unsubscribe, updateDoc, getDoc, deleteDoc, query, orderBy, limit, getDocs, startAfter, DocumentData, DocumentSnapshot, QuerySnapshot, getCountFromServer, runTransaction, where, docData } from '@angular/fire/firestore';
import { FirebaseAuthService } from './firebase.auth.service';
import { CartItem, DeliveryLocation, GroceryCategorySortRanking, IMessage, OrderDetails, OrderPaymentIntent, OrderStatus, OrderStatusProgressOrder, PricingMetrics, UserProfile, Batch } from '../constants';
import { firstValueFrom, forkJoin, mergeMap, Observable, Observer, of, take, tap, BehaviorSubject } from 'rxjs';
import { FirebaseFunctionsService } from './firebase.functions.service';
import { User } from '@angular/fire/auth';
import { UtilityService } from './utility.service';

@Injectable({
  providedIn: 'root'
})
export class FirebaseFirestoreService {
    private firestore = inject(Firestore);
    private firebaseAuthService = inject(FirebaseAuthService);
    private firebaseFunctionsService = inject(FirebaseFunctionsService);
    private utilityService = inject(UtilityService);
    userDocumentRef: DocumentReference | undefined;
    activeCartItems$: Observable<CartItem[]> | undefined;
    activeCartItemsCollection: CollectionReference | undefined;
    orders$: Observable<OrderDetails[]> | undefined;
    ordersCollection: CollectionReference | undefined;
    chatHistoryCollection: CollectionReference | undefined;
    chatHistoryDocRef: DocumentReference | undefined;
    userProfileSnapshotUnsubscriber: Unsubscribe | undefined;
    lastRetrievedChatMessage: DocumentData | undefined;

    lastRetrievedOpenOrder: DocumentData | undefined;
    lastRetrievedCompletedOrder: DocumentData | undefined;
    lastRetrievedOrder: DocumentData | undefined;
    private userProfile$ = new BehaviorSubject<UserProfile|null>(null);
    userProfile?:UserProfile;

    constructor() {
        this.firebaseAuthService.user$.subscribe(async (user) => {
            console.log('FirebaseFirestoreService', user)
            if(!user || !user.uid || !user.phoneNumber) {
                this.userProfile$.next(null);
                return;
            };
            this.activeCartItemsCollection = collection(this.firestore, `cart/${user.uid}/active`) as CollectionReference<DocumentData>;
            this.activeCartItems$ = collectionData(this.activeCartItemsCollection, { idField: 'id' }) as Observable<CartItem[]>;
            this.ordersCollection = collection(this.firestore, `orders/${user.uid}/grocery`);
            this.orders$ = collectionData(this.ordersCollection, { idField: 'orderId' }) as Observable<OrderDetails[]>;
            this.userDocumentRef =  doc(this.firestore, 'users', user.uid);

            this.chatHistoryCollection =  collection(this.firestore, `chat_history/${user.uid}/messages`);
            this.chatHistoryDocRef =  doc(this.firestore, `chat_history/${user.uid}`);

            this.keepLocalUserProfileUpdated();

        })
        
    }

    keepLocalUserProfileUpdated() {
        if(this.userProfileSnapshotUnsubscriber) {
            this.userProfileSnapshotUnsubscriber();
        }
        this.userProfileSnapshotUnsubscriber = onSnapshot(this.userDocumentRef as DocumentReference, (documentSnapshot) => {
            console.log('user profile', documentSnapshot.data())
            const userProfile = documentSnapshot.data() as UserProfile;
            this.userProfile = userProfile;
            this.userProfile$.next(userProfile);
            if(!userProfile.stripeCustomerId && userProfile.name) {
                this.firebaseFunctionsService.createStripeCustomer(userProfile);
            }
        })
    }

    getUserProfile() {
        return this.userProfile as UserProfile;
    }

    subscribeToUserProfile() {
        return this.userProfile$.asObservable();
    }

    getAvailableBatches(): Observable<Batch[]> {
        const batchesCollection = collection(this.firestore, 'orders/batches/grocery');
        // need to filter by location, if shopper location is 25 miles around batch deliveryCentroid location, then show the batch
        const shopperLocation = this.utilityService.location;
        if(!shopperLocation) {
            return of([]) as Observable<Batch[]>;
        }
        const q = query(batchesCollection, where('status', '==', 'available'), where('deliveryCentroid.lat', '>=', shopperLocation.latitude - 0.25), where('deliveryCentroid.lng', '>=', shopperLocation.longitude - 0.25), where('deliveryCentroid.lat', '<=', shopperLocation.latitude + 0.25), where('deliveryCentroid.lng', '<=', shopperLocation.longitude + 0.25));
        // return collectionData(q, { idField: 'batchId' }) as Observable<Batch[]>;
        // const q = query(batchesCollection, where('status', '==', 'available'));
        return collectionData(q, { idField: 'batchId' }) as Observable<Batch[]>;
    }

    getAcceptedBatches(): Observable<Batch[]> {
        const batchesCollection = collection(this.firestore, 'orders/batches/grocery');
        const shopperId = this.getUserProfile()?.uid;
        if (!shopperId) return of([]) as Observable<Batch[]>;
        const q = query(batchesCollection, where('status', 'in', ['accepted', 'in_progress']), where('shopperId', '==', shopperId));
        return collectionData(q, { idField: 'batchId' }) as Observable<Batch[]>;
    }

    getCompletedBatches(): Observable<Batch[]> {
        const batchesCollection = collection(this.firestore, 'orders/batches/grocery');
        const shopperId = this.getUserProfile()?.uid;
        if (!shopperId) return of([]) as Observable<Batch[]>;
        const q = query(batchesCollection, where('status', '==', 'completed'), where('shopperId', '==', shopperId));
        return collectionData(q, { idField: 'batchId' }) as Observable<Batch[]>;
    }

    getBatchById(batchId: string): Observable<Batch> {
        const batchDocRef = doc(this.firestore, 'orders/batches/grocery', batchId);
        return docData(batchDocRef, { idField: 'batchId' }) as Observable<Batch>;
    }

    async updateBatch(batchId: string, batch: Partial<Batch>) {
        await updateDoc(doc(this.firestore, 'orders/batches/grocery', batchId), batch);
    }

    getDocRefForPath(path: string) {
        return doc(this.firestore, path);
    }


    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getTopGroceries(): Promise<any> {
        const topGroceriesDocRef = doc(this.firestore, `/app_data/top_groceries`);
        try {
            const docRef = await getDoc(topGroceriesDocRef as DocumentReference);
            if(!docRef.exists()) {
                return undefined;
            }
            const groceryCategoryList = [];
            const groceryCategories = [];
            const groceryList: { category: string; name: string; }[] = [];
            for (const [key, value] of Object.entries(docRef.data())) {
                groceryCategories.push(key);
                groceryCategoryList.push({
                    category: key,
                    items: value
                })
                value.forEach((item: string) => {
                    groceryList.push({
                        category: key,
                        name: item
                    })
                })
            }
            console.log({
                groceryList,
                groceryCategoryList,
                groceryCategories
            })
            return {
                groceryList,
                groceryCategoryList: groceryCategoryList.sort((a,b) => {
                    const rankA = GroceryCategorySortRanking[a.category as keyof typeof GroceryCategorySortRanking] ?? Infinity;
                    const rankB = GroceryCategorySortRanking[b.category as keyof typeof GroceryCategorySortRanking] ?? Infinity;
                    return rankA - rankB
                }),
                groceryCategories: groceryCategories.sort((a,b) => {
                    const rankA = GroceryCategorySortRanking[a as keyof typeof GroceryCategorySortRanking] ?? Infinity;
                    const rankB = GroceryCategorySortRanking[b as keyof typeof GroceryCategorySortRanking] ?? Infinity;
                    return rankA - rankB
                })
            };
            
        } catch (error) {
            console.log(error);
            return undefined;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getUserGroceryHistory(uid?: string): Promise<any> {
        const userGroceryHistoryCollection = collection(this.firestore, `/app_data/grocery_items/${uid || this.getUserProfile()?.uid}`);
        const userGroceryHistory$ = collectionData(userGroceryHistoryCollection);
        const groceryItems: (DocumentData | (DocumentData & {}))[] = [];
        try {
            await userGroceryHistory$.pipe(take(1)).forEach((item) => {
                groceryItems.push(...item);
            })
            return groceryItems;
            
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    async getPricingMetrics(): Promise<PricingMetrics> {
        const pricingMetricsCollection = collection(this.firestore, `/pricing_metrics`);
        const pricingMetrics$ = collectionData(pricingMetricsCollection, { idField: 'metricType' });
        const pricingMetrics = {} as PricingMetrics;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await pricingMetrics$.pipe(take(1)).forEach((metrics: any[]) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            metrics.forEach((metric: any) => {
                const type = metric.metricType as string;
                switch (type) {
                    case 'shopping_fee':
                        pricingMetrics.shoppingFee = metric;
                        break;
                    case 'delivery_fee':
                        pricingMetrics.deliveryFee = metric;
                        break;
                    case 'sales_tax':
                        pricingMetrics.salesTax = metric;
                        break;
                    case 'service_fee':
                        pricingMetrics.serviceFee = metric;
                        break;
                    default:
                        break;
                }
            })
            
        })
        return pricingMetrics;
    }

    async addItemToCart(prompt: string, data: Partial<CartItem>) {
        try {
            if(!this.activeCartItemsCollection) {
                return false;
            }
            if(data.id) {
                await setDoc(doc(this.firestore, this.activeCartItemsCollection.path + `/${data.id}`), {userPrompt: prompt, ...data, fromPastOrders: true } as CartItem, {merge: true})
                return data.id;
            } else {
                const response = await addDoc(this.activeCartItemsCollection, { userPrompt: prompt, ...data } as CartItem);
                return response.id;
            }
            
        } catch(error: unknown) {
            console.log(error);
            return false;
        }
    }

    async updateCartItem(id: string, data: Partial<CartItem>) {
        const cartItemDocumentRef = doc(this.firestore, this.activeCartItemsCollection?.path as string, id as string);
        try {
            await updateDoc(cartItemDocumentRef as DocumentReference, data);
            return true;
        } catch(error: unknown) {
            console.log(error);
            return false;
        }
    }

    getCartItemRef(id: string) {
        return doc(this.firestore, this.activeCartItemsCollection?.path as string, id as string);
    }

    async removeItemFromCart(id: string) {
        try {
            if(!id) {
                return false;
            }
            await deleteDoc(doc(this.firestore, `cart/${this.getUserProfile().uid}/active`, id ));
            return of(true);
        } catch(error: unknown) {
            console.log(error);
            return of(false);
        }
    }

    async emptyCart() {
        try {
            const items = await firstValueFrom(this.getActiveCartItems());
            await Promise.all(items.map((item) => this.removeItemFromCart(item.id as string)));
            return of(true);
        } catch(error: unknown) {
            console.log(error);
            return of(false);
        }
    }

    getActiveCartItems(): Observable<CartItem[]> {
        if(!this.activeCartItems$) {
            return of([]);
        }
        return this.activeCartItems$.pipe(
            tap(i =>  console.log(i))
        );
    }

    async getOrdersCount() {
        if(!this.ordersCollection) {
            return 0;
        }
        return (await getCountFromServer(this.ordersCollection)).data().count;
    }

    async getOrders() {
        try {
            if(!this.lastRetrievedOrder) {
                const first = query(
                    this.ordersCollection as CollectionReference, 
                    orderBy("orderedDateTime", 'desc'), 
                    limit(25)
                );
                const documentSnapshots = await getDocs(first);
        
                // Get the last visible document
                this.lastRetrievedOrder = documentSnapshots.docs[documentSnapshots.docs.length-1];
                // return from(documentSnapshots.docs).pipe(
                //     mergeMap(doc => getDoc(doc.data()['orderDocumentRef']),
                //     map((doc) => doc)
                // ) //.map((d) => ({...d.data()})) as OrderDetails[];
                const orders: OrderDetails[] = [];
                for(let i = 0; i< documentSnapshots.size; i++) {
                    const orderData = documentSnapshots.docs[i].data() as OrderDetails;
                    orders.push({...orderData, orderId: documentSnapshots.docs[i].id, orderDocumentRef: documentSnapshots.docs[i].ref});
                };
                return orders;
            } else {
                const next = query(
                    this.ordersCollection as CollectionReference,
                    orderBy("orderedDateTime", 'desc'),
                    startAfter(this.lastRetrievedOpenOrder),
                    limit(25)
                );
                const documentSnapshots = await getDocs(next);
        
                // Get the last visible document
                this.lastRetrievedOrder = documentSnapshots.docs[documentSnapshots.docs.length-1];
                // return documentSnapshots.docs.map((d) => ({...d.data()})) as OrderDetails[];
                const orders = [];
                for(let i = 0; i< documentSnapshots.size; i++) {
                    const orderData = documentSnapshots.docs[i].data() as OrderDetails;
                    orders.push({...orderData, orderId: documentSnapshots.docs[i].id, orderDocumentRef: documentSnapshots.docs[i].ref});
                };
                return orders;
            }
        } catch(error: unknown) {
            console.log(error);
            return false;
        }
    }

    subscribeToOrderDetails(orderDocumentRef: DocumentReference, observer: Observer<DocumentSnapshot>) {
        return onSnapshot(orderDocumentRef, observer);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    openOrdersCollectionSnapshot(snapshotCallback:(snapshot: QuerySnapshot) => void) {
        const openOrderCollectionRef = collection(this.firestore, `orders/open_orders/grocery`);
        return onSnapshot(openOrderCollectionRef, snapshotCallback);
    }

    async getOrderDetails(orderDocumentRef: DocumentReference) {
        // const orderDocumentRef = doc(this.firestore, this.ordersCollection?.path as string, orderId );
        try {
            const docRef = await getDoc(orderDocumentRef as DocumentReference);
            if(!docRef.exists()) {
                return undefined;
            }
            return {...docRef.data(), orderId: docRef.id, orderDocumentRef: docRef.ref} as OrderDetails;
            
        } catch (error) {
            console.log(error);
            return undefined;
        }

    }

    async addToOrders(orderDetails: OrderDetails) {
        try {
            if(!this.ordersCollection) {
                return null;
            }
            const docRef = await addDoc(this.ordersCollection, {
                ...orderDetails,
                stripeCustomerId: this.getUserProfile().stripeCustomerId,
                customerPreferences: this.getUserProfile().personalization,
                createdById: this.getUserProfile().uid,
                createdByName: this.getUserProfile().name,
                orderedDateTime: new Date().valueOf(),
                orderedDateTimeString: new Date().toLocaleString(),
                orderUpdatedDateTime: new Date().valueOf(),
                orderUpdatedDateTimeString: new Date().toLocaleString()
            } as OrderDetails);
            const newOrderDetails = await this.getOrderDetails(docRef);
            this.addToUserGroceryItemsHistory(newOrderDetails as OrderDetails);
            // if(!orderDetails.deliveryLocation?.uid) {
            //     this.addToUserPreviousAddresses(orderDetails.deliveryLocation as DeliveryLocation);
            // } else {
            //     
            // }
            this.updateLastUsedPreviousAddress(orderDetails.deliveryLocation.uid as string, orderDetails.deliveryLocation);
            return newOrderDetails;
        } catch(error: unknown) {
            console.log(error);
            return null;
        }
    }

    async updateOrderDetails(orderDocumentRef: DocumentReference, orderDetails: Partial<OrderDetails>) {
        // const orderDocumentRef = doc(this.firestore, this.ordersCollection?.path as string, orderId );
        try {
            await updateDoc(orderDocumentRef as DocumentReference, {
                ...orderDetails,
                orderUpdatedById: this.getUserProfile().uid,
                orderUpdatedDateTime: new Date().valueOf(),
                orderUpdatedDateTimeString: new Date().toLocaleString()
            });
            return true;
        } catch(error: unknown) {
            console.log(error);
            return false;
        }
    }

    async getLastActiveOrder() {
        const userOrdersCollection = collection(this.firestore, `orders/${this.getUserProfile().uid}/grocery`);

        try {
            const activeOrderQuery = query(
                userOrdersCollection,
                where('orderStatus', '!=', 'delivered'),
                orderBy('orderedDateTime', 'desc'),
                limit(1)
            );

            const snapshot = await getDocs(activeOrderQuery);

            if (!snapshot.empty) {
                return {...snapshot.docs[0].data(), orderDocumentRef: snapshot.docs[0].ref} as OrderDetails;
            }

            return null;
            
        } catch(error: unknown) {
            console.log(error);
            return null;
        }
    }

    async getOpenOrders() {
        const openOrdersCollection = collection(this.firestore, `orders/open_orders/grocery`);

        try {
            if(!this.lastRetrievedOpenOrder) {
                const first = query(
                    openOrdersCollection, 
                    orderBy("orderedDateTime", 'desc'), 
                    limit(25)
                );
                const documentSnapshots = await getDocs(first);
        
                // Get the last visible document
                this.lastRetrievedOpenOrder = documentSnapshots.docs[documentSnapshots.docs.length-1];
                // return from(documentSnapshots.docs).pipe(
                //     mergeMap(doc => getDoc(doc.data()['orderDocumentRef']),
                //     map((doc) => doc)
                // ) //.map((d) => ({...d.data()})) as OrderDetails[];
                const openOrders: OrderDetails[] = [];
                for(let i = 0; i< documentSnapshots.size; i++) {
                    const order = await getDoc(documentSnapshots.docs[i].data()['orderDocumentRef'] as DocumentReference);
                    const orderData = order.data() as OrderDetails;
                    openOrders.push({...orderData, orderId: order.id, ...documentSnapshots.docs[i].data()});
                };
                return openOrders;
            } else {
                const next = query(
                    openOrdersCollection,
                    orderBy("orderedDateTime", 'desc'),
                    startAfter(this.lastRetrievedOpenOrder),
                    limit(25)
                );
                const documentSnapshots = await getDocs(next);
        
                // Get the last visible document
                this.lastRetrievedOpenOrder = documentSnapshots.docs[documentSnapshots.docs.length-1];
                // return documentSnapshots.docs.map((d) => ({...d.data()})) as OrderDetails[];
                const openOrders = [];
                for(let i = 0; i< documentSnapshots.size; i++) {
                    const order = await getDoc(documentSnapshots.docs[i].data()['orderDocumentRef'] as DocumentReference);
                    const orderData = order.data() as OrderDetails;
                    openOrders.push({...orderData, orderId: order.id});
                };
                return openOrders;
            }
        } catch(error: unknown) {
            console.log(error);
            return false;
        }
    }

    resetLastRetrievedOpenOrder(){
        this.lastRetrievedOpenOrder = undefined;
    }

    async addToOpenOrders(orderDetails: OrderDetails) {
        try {
            const openOrdersDocumentRef =  doc(this.firestore, `orders/open_orders/grocery/${orderDetails.orderId}`);
            await setDoc(openOrdersDocumentRef as DocumentReference, {
                orderDocumentRef: orderDetails.orderDocumentRef,
                orderedDateTime: orderDetails.orderedDateTime,
                batched: false
            }, { merge : true });
            return orderDetails;
        } catch(error: unknown) {
            console.log(error);
            return null;
        }
    }

    async removeFromOpenOrders(orderDetails: OrderDetails) {
        try {
            const openOrdersDocumentRef =  doc(this.firestore, `orders/open_orders/grocery/${orderDetails.orderId}`);
            await deleteDoc(openOrdersDocumentRef as DocumentReference);
            return true;
        } catch(error: unknown) {
            console.log(error);
            return null;
        }
    }

    getAcceptedOrders(){
        const acceptedOrdersCollection = collection(this.firestore, `orders/${this.getUserProfile().uid}/accepted_orders`);
        try {
            return (collectionData(acceptedOrdersCollection) as Observable<{orderDocumentRef: DocumentReference, orderId: string}[]>).pipe(
                mergeMap(acceptedOrders => {
                    console.log(acceptedOrders);
                    if(acceptedOrders.length) {
                        const orderDetailsObservables = acceptedOrders.map((order) => this.getOrderDetails(order.orderDocumentRef));
                        // Use forkJoin to wait for all Observables to complete and collect their results
                        return forkJoin(orderDetailsObservables);
                    } else {
                        return of([])
                    }
                }));
        } catch(error: unknown) {
            console.log(error);
            return of(false);
        }
    }

    async addToAcceptedOrders(orderDetails: OrderDetails) {
        try {
            await runTransaction(this.firestore, async (transaction) => {
                const orderSnapshot = await transaction.get(orderDetails.orderDocumentRef as DocumentReference);
            
                if (!orderSnapshot.exists()) {
                    throw new Error('Order does not exist');
                }
            
                const order = orderSnapshot.data() as OrderDetails;
            
                if (order.orderStatus !== OrderStatus.OrderPlaced || order.acceptedById) {
                    throw new Error('Order already accepted');
                }

                const currentOrderStatusIndex = OrderStatusProgressOrder.findIndex((status) => status === orderDetails.orderStatus);
                // Update the order acceptedById with current userid
                transaction.update(orderDetails.orderDocumentRef as DocumentReference, {
                    orderStatus: OrderStatusProgressOrder[currentOrderStatusIndex + 1], 
                    orderUpdatedById: this.getUserProfile().uid,
                    orderUpdatedDateTime: new Date().valueOf(),
                    orderUpdatedDateTimeString: new Date().toLocaleString(),
                    acceptedById: this.getUserProfile().uid,
                    acceptedByName: this.getUserProfile().name,
                    acceptedByPhone: this.getUserProfile().phoneNumber
                });
            });
            console.log('Order accepted!');

            // remove from open orders collection and moved to user accepted orders collection
            const removed = await this.removeFromOpenOrders(orderDetails);
            console.log('remove from open orders collection');
            if(removed) {
                const acceptedOrdersDocumentRef =  doc(this.firestore, `orders/${this.getUserProfile().uid}/accepted_orders/${orderDetails.orderId}`);
                await setDoc(acceptedOrdersDocumentRef as DocumentReference, {
                    orderDocumentRef: orderDetails.orderDocumentRef,
                    orderId: orderDetails.orderId,
                    orderedDateTime: orderDetails.orderedDateTime,
                    acceptedDateTime: new Date().valueOf(),
                    acceptedById: this.getUserProfile().uid,
                    acceptedByName: this.getUserProfile().name,
                    acceptedByPhone: this.getUserProfile().phoneNumber
                }, { merge : true });
    
                this.lastRetrievedOpenOrder = undefined;
                console.log('moved to user accepted orders collection');
            }
            return { success: true };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (err: any) {
            console.error('Failed to accept order:', err.message);
            addDoc(collection(orderDetails.orderDocumentRef as DocumentReference, 'accept_attempts'), {
                shopperId: this.getUserProfile().uid,
                attemptedAt: new Date().valueOf(),
                attemptedAtString: new Date().toISOString(),
                result: 'failed',
                message: err.message
            });
            return { success: false, error: err.message };
          }
    }

    async removeFromAcceptedOrders(orderDetails: OrderDetails) {
        try {
            const acceptedOrdersDocumentRef =  doc(this.firestore, `orders/${this.getUserProfile().uid}/accepted_orders/${orderDetails.orderId}`);
            await deleteDoc(acceptedOrdersDocumentRef as DocumentReference);
            return true;
        } catch(error: unknown) {
            console.log(error);
            return null;
        }
    }

    async addToCompletedOrders(orderDetails: OrderDetails) {
        try {
            const completedOrdersDocumentRef =  doc(this.firestore, `orders/${this.getUserProfile().uid}/completed_orders/${orderDetails.orderId}`);
            const removed = await this.removeFromAcceptedOrders(orderDetails);
            if(!removed) {
                return null;
            }
            await setDoc(completedOrdersDocumentRef as DocumentReference, {
                orderDocumentRef: orderDetails.orderDocumentRef,
                orderId: orderDetails.orderId,
                orderedDateTime: orderDetails.orderedDateTime,
                completedDateTime: new Date().valueOf(),
                completedById: this.getUserProfile().uid,
                completedByPhone: this.getUserProfile().phoneNumber
            }, { merge : true });
            const currentOrderStatusIndex = OrderStatusProgressOrder.findIndex((status) => status === orderDetails.orderStatus);
            await this.updateOrderDetails(orderDetails.orderDocumentRef as DocumentReference, {
                orderStatus: OrderStatusProgressOrder[currentOrderStatusIndex + 1],
                deliveryDateTime: new Date().valueOf(),
                deliveryDateTimeString: new Date().toLocaleString(),
                completedById: this.getUserProfile().uid
            });
            this.lastRetrievedOpenOrder = undefined;
            return orderDetails;
        } catch(error: unknown) {
            console.log(error);
            return null;
        }
    }

    async addToUserGroceryItemsHistory(orderDetails: OrderDetails) {
        await Promise.all(
            orderDetails.items.map(item => setDoc(doc(this.firestore, `/app_data/grocery_items/${orderDetails.createdById}/${item.id}`), {...item, orderedOn: orderDetails.orderedDateTime}, {merge: true}))
        );
    }

    resetLastRetrievedCompletedOrder(){
        this.lastRetrievedCompletedOrder = undefined;
    }

    async getCompletedOrders() {
        const completedOrdersCollection = collection(this.firestore, `orders/${this.getUserProfile().uid}/completed_orders`);

        try {
            if(!this.lastRetrievedCompletedOrder) {
                const first = query(
                    completedOrdersCollection, 
                    orderBy("completedDateTime", 'desc'), 
                    limit(25)
                );
                const documentSnapshots = await getDocs(first);
        
                // Get the last visible document
                this.lastRetrievedCompletedOrder = documentSnapshots.docs[documentSnapshots.docs.length-1];
                // return from(documentSnapshots.docs).pipe(
                //     mergeMap(doc => getDoc(doc.data()['orderDocumentRef']),
                //     map((doc) => doc)
                // ) //.map((d) => ({...d.data()})) as OrderDetails[];
                const completedOrders: OrderDetails[] = [];
                for(let i = 0; i< documentSnapshots.size; i++) {
                    const order = await getDoc(documentSnapshots.docs[i].data()['orderDocumentRef'] as DocumentReference);
                    const orderData = order.data() as OrderDetails;
                    completedOrders.push({...orderData, orderId: order.id, ...documentSnapshots.docs[i].data()});
                };
                return completedOrders;
            } else {
                const next = query(
                    completedOrdersCollection,
                    orderBy("completedDateTime", 'desc'),
                    startAfter(this.lastRetrievedOpenOrder),
                    limit(25)
                );
                const documentSnapshots = await getDocs(next);
        
                // Get the last visible document
                this.lastRetrievedCompletedOrder = documentSnapshots.docs[documentSnapshots.docs.length-1];
                // return documentSnapshots.docs.map((d) => ({...d.data()})) as OrderDetails[];
                const completedOrders = [];
                for(let i = 0; i< documentSnapshots.size; i++) {
                    const order = await getDoc(documentSnapshots.docs[i].data()['orderDocumentRef'] as DocumentReference);
                    const orderData = order.data() as OrderDetails;
                    completedOrders.push({...orderData, orderId: order.id});
                };
                return completedOrders;
            }
        } catch(error: unknown) {
            console.log(error);
            return false;
        }
    }

    async moveOrderToNextStatus(order: OrderDetails) {
        if(order.orderStatus === OrderStatus.Delivered) return;
        const currentOrderStatusIndex = OrderStatusProgressOrder.findIndex((status) => status === order.orderStatus);
        if(order.orderStatus === OrderStatus.OrderPlaced) {
            await this.addToAcceptedOrders(order);
        } else {
            const nextOrderStatus = OrderStatusProgressOrder[currentOrderStatusIndex + 1];
            if(nextOrderStatus === OrderStatus.Delivered) {
                await this.addToCompletedOrders(order);
            } else if(nextOrderStatus === OrderStatus.ShoppingCompleted) {
                const total = this.calculateOrderTotal(order);
                await this.updateOrderDetails(order.orderDocumentRef as DocumentReference, { orderStatus: nextOrderStatus, total});
                await this.chargeCustomer({...order, total});
            }  else {
                await this.updateOrderDetails(order.orderDocumentRef as DocumentReference, { orderStatus: nextOrderStatus});
            }
        }
    }

    async chargeCustomer(order: OrderDetails) {
        try {
            const paymentIntent = await this.firebaseFunctionsService.createChargeOffSession({
                amount: order?.total || 9,
                currency: 'USD',
                paymentMethodId: order?.paymentMethodId || '',
                returnUrl: '',
                stripeCustomerId: order.stripeCustomerId
            });
            await this.updateOrderDetails(order.orderDocumentRef as DocumentReference, { paymentIntent: paymentIntent as OrderPaymentIntent, PaymentStatus: 'success'});
        } catch(err: unknown) {
            await this.updateOrderDetails(order.orderDocumentRef as DocumentReference, { PaymentStatus: 'failed'} );
            console.log(err);
        }
        
        
    }

    calculateOrderTotal(order: OrderDetails) {
        const storeReceiptsTotal = order.storeReceipts?.reduce((pre, cur) => pre + Number(cur.total), 0) || 0;
        console.log('storeReceiptsTotal', storeReceiptsTotal);
        return order.totalFee + storeReceiptsTotal;
    }

    calculateOrderPricing(items: CartItem[], pricingMetrics: PricingMetrics) {
        if(!pricingMetrics) return;
    
        const uniqueStores = items.filter((cartItem, index, self) => index === self.findIndex((item) => item.store?.toLowerCase() === cartItem.store?.toLowerCase()));
        const stores = uniqueStores.filter((item) => item.store !== '').map(item => item.store + ', ' + item.location || ''),
        storeCount = stores.length,
        storeLocations = [...new Set(uniqueStores.map(item => item.location?.toLowerCase() || ''))],
        totalCartItems = items.length,
        shoppingFee = pricingMetrics.shoppingFee.minimum,
        multiStoreShoppingFee = storeCount > 1 ? (storeCount - 1) * pricingMetrics.shoppingFee.perAdditionalStore : 0,
        totalShoppingFee = shoppingFee + multiStoreShoppingFee,
        deliveryFee = pricingMetrics.deliveryFee.minimum,
        multiStoreDeliveryFee = storeCount > 1 ? (storeCount - 1) * pricingMetrics.deliveryFee.perAdditionalStore : 0,
        totalDeliveryFee = deliveryFee + multiStoreDeliveryFee,
        serviceFee = Math.min(Math.max(pricingMetrics.serviceFee.minimum, (((totalShoppingFee + deliveryFee) * pricingMetrics.serviceFee.percent)/100)), pricingMetrics.serviceFee.maximum),
        salesTax = pricingMetrics.salesTax.california,
        totalFee = totalShoppingFee + totalDeliveryFee + serviceFee + salesTax;
        
        return {
          shoppingFee,
          multiStoreShoppingFee,
          totalShoppingFee,
          totalCartItems,
          serviceFee,
          salesTax,
          deliveryFee,
          totalDeliveryFee,
          multiStoreDeliveryFee,
          stores: stores.map(store => ({
            name: store,
            allItemsMarked: false,
            hasReceipt: false,
            shoppingCompleted: false
          })),
          storeCount,
          storeLocations,
          totalFee
        }
      }

    // CHAT HISTORY

    async addToChatHistory(message: IMessage) {
        console.log(message);
        if(!this.chatHistoryCollection) {
            return;
        }
        try {
            const docRef = await addDoc(this.chatHistoryCollection, message);
            await setDoc(this.chatHistoryDocRef as DocumentReference, {
                lastMessage: message,
                lastMessageUid: docRef.id
            },{ merge : true });

            // set lastRetrievedChatMessage if user not retrieved any yet to set the starting point for next retrieval
            // if(!this.lastRetrievedChatMessage) {
            //     const docSnapshot = await getDoc(this.userDocumentRef as DocumentReference);
            //     this.lastRetrievedChatMessage = docSnapshot.data();
            // }
            return true;
        } catch (error: unknown) {
            console.log(error);
            return false;
        }
    }

    async getChatHistory() {
        if(!this.chatHistoryCollection) {
            return;
        }
        try {
            if(!this.lastRetrievedChatMessage) {
                const first = query(
                    this.chatHistoryCollection, 
                    orderBy("timestamp", 'desc'), 
                    limit(25)
                );
                const documentSnapshots = await getDocs(first);
        
                // Get the last visible document
                this.lastRetrievedChatMessage = documentSnapshots.docs[documentSnapshots.docs.length-1];
                console.log("lastRetrievedChatMessage", this.lastRetrievedChatMessage);
                console.log('documentSnapshots.docs', documentSnapshots.docs)
                return documentSnapshots.docs.map((d) => d.data()) as IMessage[];
            } else {
                const next = query(
                    this.chatHistoryCollection,
                    orderBy("timestamp", 'desc'),
                    startAfter(this.lastRetrievedChatMessage),
                    limit(25)
                );
                const documentSnapshots = await getDocs(next);
        
                // Get the last visible document
                this.lastRetrievedChatMessage = documentSnapshots.docs[documentSnapshots.docs.length-1];
                console.log("lastRetrievedChatMessage", this.lastRetrievedChatMessage);
                console.log('documentSnapshots.docs', documentSnapshots.docs)
                return documentSnapshots.docs.map((d) => d.data()) as IMessage[];
            }
        } catch(error: unknown) {
            console.log(error);
            return false;
        }
    }

    async getUserPreviousAddresses(): Promise<DeliveryLocation[]> {
        const userAddressCollectionRef = collection(this.firestore, `${this.userDocumentRef?.path}/addresses`);

        const userAddressCollection$ = collectionData(userAddressCollectionRef, {idField: 'uid'});
        const userAddresses: (DocumentData | (DocumentData & {}))[] = [];
        try {
            await userAddressCollection$.pipe(take(1)).forEach((item) => {
                userAddresses.push(...item);
            })
            return userAddresses as DeliveryLocation[];
            
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    async addToUserPreviousAddresses(address: DeliveryLocation) {
        const userAddressCollectionRef = collection(this.firestore, `${this.userDocumentRef?.path}/addresses`);
        const doc = await addDoc(userAddressCollectionRef, {...address, createdOn: new Date().valueOf(), lastUsed: new Date().valueOf()});
        return doc.id;
    }

    async deleteUserPreviousAddress(uid: string) {
        const userAddressDocRef = doc(this.firestore, `${this.userDocumentRef?.path}/addresses/${uid}`);
        await deleteDoc(userAddressDocRef);
    }

    async updateLastUsedPreviousAddress(uid: string, address: DeliveryLocation) {
        const userAddressDocRef = doc(this.firestore, `${this.userDocumentRef?.path}/addresses/${uid}`);
        await updateDoc(userAddressDocRef, {...address, lastUsed: new Date().valueOf()})
    }

    pushToDailyAvailabilityLogs(status: boolean) {
        const userAvailabilityLogsCollectionRef = collection(this.firestore, `${this.userDocumentRef?.path}/availability_logs`);
        addDoc(userAvailabilityLogsCollectionRef, {status, createdOn: new Date().valueOf(), createdOnString: new Date().toISOString()});
    }

    async updateUserDocument(userProfile: Partial<UserProfile>) {
        if(!this.userDocumentRef) return;
        
        try {
            await updateDoc(this.userDocumentRef as DocumentReference, userProfile);
            return true;
        } catch(error: unknown) {
            console.log(error);
            return false;
        }
    }

    async getUserDocument(authUser: User) {
        try {
            this.userDocumentRef =  doc(this.firestore, 'users', authUser.uid as string);
            const userDocRef = await getDoc(this.userDocumentRef as DocumentReference); 
            return userDocRef.data() as UserProfile;
        } catch(error: unknown) {
            console.log(error);
            return null;
        }
    }

    async addDeviceFCMToken(userUid: string, tokenData: {data: string, deviceModel: string, type: string}) {
        await setDoc(doc(this.firestore, 'users', userUid ,`fcm_tokens/${tokenData.data}`), {
            token: tokenData.data,
            deviceModel: tokenData.deviceModel || "",
            deviceOS: tokenData.type || '',
            updatedAt: new Date().toISOString(),
        });
    }

    async addLocationRequest(userUid: string, location: {latitude: number, longitude: number}, requested: boolean) {
        await addDoc(collection(this.firestore, 'location_requests'), {
            userUid,
            latitude: location.latitude,
            longitude: location.longitude,
            requested,
            createdAt: new Date().toISOString(),
        });
    }
}